import React, { forwardRef } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl' | 'icon' | 'icon-sm' | 'icon-lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: `
    bg-gradient-to-br from-primary-600 to-primary-500
    text-white
    shadow-sm shadow-primary
    hover:from-primary-700 hover:to-primary-600 hover:shadow-md hover:shadow-primary hover:-translate-y-0.5
    active:scale-[0.98]
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
    dark:focus-visible:ring-offset-gray-900
  `,
  secondary: `
    bg-surface-base
    text-text-primary
    border-2 border-gray-300
    shadow-xs
    hover:border-primary-500 hover:text-primary-700 hover:bg-primary-50 hover:shadow-sm
    dark:hover:bg-primary-900/20 dark:hover:text-primary-400 dark:border-gray-600
    active:scale-[0.98]
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
    dark:focus-visible:ring-offset-gray-900
  `,
  accent: `
    bg-gradient-to-br from-accent-600 to-accent-500
    text-white
    shadow-sm shadow-accent
    hover:from-accent-700 hover:to-accent-600 hover:shadow-md hover:shadow-accent hover:-translate-y-0.5
    active:scale-[0.98]
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2
    dark:focus-visible:ring-offset-gray-900
  `,
  ghost: `
    bg-transparent
    text-text-secondary
    hover:bg-gray-100 hover:text-text-primary
    dark:hover:bg-gray-800
    active:scale-[0.98]
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
    dark:focus-visible:ring-offset-gray-900
  `,
  danger: `
    bg-gradient-to-br from-error-600 to-error-500
    text-white
    shadow-sm
    hover:from-error-700 hover:to-error-600 hover:shadow-md hover:-translate-y-0.5
    active:scale-[0.98]
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error-500 focus-visible:ring-offset-2
    dark:focus-visible:ring-offset-gray-900
  `,
  success: `
    bg-gradient-to-br from-success-600 to-success-500
    text-white
    shadow-sm
    hover:from-success-700 hover:to-success-600 hover:shadow-md hover:-translate-y-0.5
    active:scale-[0.98]
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-500 focus-visible:ring-offset-2
    dark:focus-visible:ring-offset-gray-900
  `,
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg',
  xl: 'px-10 py-5 text-xl font-bold',
  icon: 'p-3 w-10 h-10',
  'icon-sm': 'p-2 w-8 h-8',
  'icon-lg': 'p-4 w-12 h-12',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const isIconButton = size.startsWith('icon');

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={`
          inline-flex items-center justify-center gap-2
          font-semibold leading-none
          rounded-xl
          border-0 cursor-pointer
          transition-all duration-fast ease-out
          whitespace-nowrap select-none
          ${variantStyles[variant]}
          ${sizeStyles[size]}
          ${fullWidth ? 'w-full' : ''}
          ${isDisabled ? 'opacity-50 cursor-not-allowed transform-none hover:transform-none' : ''}
          ${className}
        `.replace(/\s+/g, ' ').trim()}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {!loading && leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
        {!isIconButton && children}
        {isIconButton && !leftIcon && !rightIcon && children}
        {!loading && rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

// Convenience exports for button groups
export const ButtonGroup: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={`inline-flex items-center gap-2 ${className}`}>{children}</div>
);

// Icon button wrapper for convenience
export const IconButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'size'> & { size?: 'sm' | 'md' | 'lg' }
>(({ size = 'md', ...props }, ref) => {
  const iconSize = size === 'sm' ? 'icon-sm' : size === 'lg' ? 'icon-lg' : 'icon';
  return <Button ref={ref} size={iconSize} {...props} />;
});

IconButton.displayName = 'IconButton';

export default Button;
